import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Auth } from '@aws-amplify/auth'
import { Hub } from '@aws-amplify/core'
import { useNavigate, useLocation } from 'react-router-dom'
import { environment } from '@/config/environment'

// Sign out after this long with no mouse or keyboard activity in the tab.
export const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000
// Cognito access and ID tokens last 60 minutes. Refresh this far ahead of expiry
// so the idle window is not cut short. The refresh token itself lasts 30 days.
export const SESSION_REFRESH_LEAD_MS = 10 * 60 * 1000
const SESSION_REFRESH_RETRY_MS = 60_000

function refreshFailureMessage(payload) {
  const detail = payload?.data ?? payload
  if (!detail) return 'token refresh failed'
  if (typeof detail === 'string') return detail
  return detail.message || detail.name || 'token refresh failed'
}

export function msUntilSessionRefresh(expirationEpochSeconds, nowMs = Date.now()) {
  const expiresAtMs = expirationEpochSeconds * 1000
  return Math.max(expiresAtMs - nowMs - SESSION_REFRESH_LEAD_MS, 0)
}

let amplifyConfigured = false
function ensureAmplifyConfigured() {
  if (amplifyConfigured) return
  Auth.configure({
    region: environment.AWS_REGION,
    identityPoolId: environment.COGNITO_IDENTITY_POOL_ID,
    userPoolId: environment.COGNITO_USER_POOL_ID,
    userPoolWebClientId: environment.COGNITO_USER_POOL_WEB_CLIENT_ID,
  })
  amplifyConfigured = true
}

const AUTH_CHALLENGES = new Set(['SOFTWARE_TOKEN_MFA', 'MFA_SETUP', 'NEW_PASSWORD_REQUIRED'])

function challengeFromCognitoUser(cogUser) {
  const challengeName = cogUser?.challengeName
  return AUTH_CHALLENGES.has(challengeName) ? challengeName : undefined
}

const AuthContext = createContext()

export function AuthProvider({ children }) {
  ensureAmplifyConfigured()

  const navigate = useNavigate()
  const location = useLocation()

  const [cognitoUser, setCognitoUser] = useState(null)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState([])
  const [authState, setAuthState] = useState({
    challenge: undefined,
    isAuthed: false,
    emailVerified: false,
  })

  const sessionRefreshTimerRef = useRef(null)
  const sessionRefreshGenerationRef = useRef(0)
  const inactivityTimerRef = useRef(null)
  const tokenRefreshListenersRef = useRef([])
  const cognitoUserRef = useRef(null)

  const replaceCognitoUser = useCallback((nextUser) => {
    cognitoUserRef.current = nextUser
    setCognitoUser(nextUser)
  }, [])

  const cancelSessionRefresh = useCallback(() => {
    sessionRefreshGenerationRef.current += 1
    if (sessionRefreshTimerRef.current) {
      clearTimeout(sessionRefreshTimerRef.current)
      sessionRefreshTimerRef.current = null
    }
  }, [])

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
  }, [])

  const signOutInternal = useCallback(async () => {
    try { await Auth.signOut() } catch { /* ignore */ }
    replaceCognitoUser(null)
    setUser(null)
    setGroups([])
    setAuthState({ challenge: undefined, isAuthed: false, emailVerified: false })
    cancelSessionRefresh()
    clearInactivityTimer()
  }, [cancelSessionRefresh, clearInactivityTimer, replaceCognitoUser])

  const scheduleSessionRefresh = useCallback(async () => {
    const generation = ++sessionRefreshGenerationRef.current
    if (sessionRefreshTimerRef.current) {
      clearTimeout(sessionRefreshTimerRef.current)
      sessionRefreshTimerRef.current = null
    }

    const stillCurrent = () => generation === sessionRefreshGenerationRef.current
    const arm = (delayMs) => {
      if (!stillCurrent()) return
      sessionRefreshTimerRef.current = window.setTimeout(() => {
        void scheduleSessionRefresh()
      }, delayMs)
    }

    try {
      let session = await Auth.currentSession()
      if (!stillCurrent()) return
      let delay = msUntilSessionRefresh(session.getAccessToken().getExpiration())
      if (delay === 0) {
        const currentUser = await Auth.currentAuthenticatedUser()
        try {
          session = await new Promise((resolve, reject) => {
            currentUser.refreshSession(session.getRefreshToken(), (err, nextSession) => {
              if (err) reject(err)
              else resolve(nextSession)
            })
          })
        } catch (err) {
          if (!stillCurrent()) return
          console.warn('Session ended: refresh token failed', {
            message: err?.message || String(err),
          })
          await signOutInternal()
          navigate('/login')
          return
        }
        if (!stillCurrent()) return
        delay = msUntilSessionRefresh(session.getAccessToken().getExpiration())
      }
      arm(delay > 0 ? delay : SESSION_REFRESH_RETRY_MS)
    } catch {
      arm(SESSION_REFRESH_RETRY_MS)
    }
  }, [navigate, signOutInternal])

  const resetInactivityTimerRef = useRef(() => {})
  resetInactivityTimerRef.current = () => {
    clearInactivityTimer()
    inactivityTimerRef.current = window.setTimeout(() => {
      console.warn('Session ended: idle timeout expired', {
        idleMinutes: INACTIVITY_TIMEOUT_MS / 60_000,
      })
      signOutInternal().then(() => navigate('/login'))
    }, INACTIVITY_TIMEOUT_MS)
  }

  const extractUserDetails = useCallback(async (cogUser) => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser()
      const attrs = currentUser.attributes || {}
      const session = await Auth.currentSession()
      const userGroups = session.getAccessToken().payload['cognito:groups'] || []

      const details = {
        id: attrs.sub,
        sub: attrs.sub,
        name: `${attrs.given_name || ''} ${attrs.family_name || ''}`.trim(),
        email: attrs.email,
        firstName: attrs.given_name || '',
        lastName: attrs.family_name || '',
      }

      setUser(details)
      setGroups(userGroups)
      return { details, groups: userGroups }
    } catch {
      return null
    }
  }, [])

  const checkEmailVerified = useCallback(async (cogUser) => {
    try {
      const verifiedContacts = await Auth.verifiedContact(cogUser)
      return !!verifiedContacts?.verified?.email
    } catch {
      return false
    }
  }, [])

  // Check for existing session on mount
  useEffect(() => {
    ;(async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser()
        const session = await Auth.currentSession()
        if (session.isValid()) {
          replaceCognitoUser(currentUser)
          const emailVerified = await checkEmailVerified(currentUser)
          setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
          await extractUserDetails(currentUser)
        }
      } catch {
        setAuthState({ challenge: undefined, isAuthed: false, emailVerified: false })
      } finally {
        setLoading(false)
      }
    })()
  }, [checkEmailVerified, extractUserDetails, replaceCognitoUser])

  // Hub listener for token refresh and sign events
  useEffect(() => {
    const listener = Hub.listen('auth', async (data) => {
      switch (data.payload.event) {
        case 'tokenRefresh':
          await extractUserDetails(cognitoUser)
          try {
            const jwt = await getUserJWTInternal()
            tokenRefreshListenersRef.current.forEach(fn => fn(jwt))
          } catch { /* ignore */ }
          break
        case 'tokenRefresh_failure':
          console.warn('Session ended: refresh token failed', {
            message: refreshFailureMessage(data.payload),
          })
          await signOutInternal()
          navigate('/login')
          break
        case 'signOut':
          cancelSessionRefresh()
          clearInactivityTimer()
          break
      }
    })
    return () => listener()
  }, [cognitoUser, extractUserDetails, signOutInternal, cancelSessionRefresh, clearInactivityTimer, navigate])

  // Idle time keeps counting while this tab is in the background. Mouse and keyboard activity resets it.
  useEffect(() => {
    if (!(authState.isAuthed && authState.emailVerified && user?.id)) return undefined

    const handler = () => resetInactivityTimerRef.current()
    window.addEventListener('mousemove', handler, true)
    window.addEventListener('keydown', handler, true)
    resetInactivityTimerRef.current()
    return () => {
      window.removeEventListener('mousemove', handler, true)
      window.removeEventListener('keydown', handler, true)
      clearInactivityTimer()
    }
  }, [authState.isAuthed, authState.emailVerified, user?.id, clearInactivityTimer])

  // Refresh the access token during the idle window so it does not expire at 60 minutes.
  useEffect(() => {
    if (!(authState.isAuthed && authState.emailVerified && user)) return undefined

    void scheduleSessionRefresh()
    const onForeground = () => {
      if (document.visibilityState === 'visible') void scheduleSessionRefresh()
    }
    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('focus', onForeground)
    return () => {
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('focus', onForeground)
      cancelSessionRefresh()
    }
  }, [authState.isAuthed, authState.emailVerified, user, scheduleSessionRefresh, cancelSessionRefresh])

  // Navigate to dashboard when fully authenticated and on login page
  useEffect(() => {
    if (authState.isAuthed && authState.emailVerified && user && location.pathname === '/login') {
      navigate('/overview')
    }
  }, [authState.isAuthed, authState.emailVerified, user, location.pathname, navigate])

  async function getUserJWTInternal() {
    const currentUser = await Auth.currentAuthenticatedUser()
    return currentUser.getSignInUserSession().getIdToken().getJwtToken()
  }

  const requireCognitoUser = useCallback(() => {
    const current = cognitoUserRef.current
    if (!current) throw new Error('No active sign-in session')
    return current
  }, [])

  const finishAuthOrChallenge = useCallback(async (cogUser) => {
    const challenge = challengeFromCognitoUser(cogUser)
    if (challenge) {
      setAuthState(prev => ({ ...prev, challenge, isAuthed: false }))
      return { challenge }
    }
    const emailVerified = await checkEmailVerified(cogUser)
    setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
    await extractUserDetails(cogUser)
    return { challenge: undefined }
  }, [checkEmailVerified, extractUserDetails])

  const signIn = useCallback(async (email, password) => {
    const cogUser = await Auth.signIn(email, password)
    replaceCognitoUser(cogUser)
    return finishAuthOrChallenge(cogUser)
  }, [finishAuthOrChallenge, replaceCognitoUser])

  const confirmSignIn = useCallback(async (totpCode) => {
    const confirmedUser = await Auth.confirmSignIn(requireCognitoUser(), totpCode, 'SOFTWARE_TOKEN_MFA')
    replaceCognitoUser(confirmedUser)
    const emailVerified = await checkEmailVerified(confirmedUser)
    setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
    await extractUserDetails(confirmedUser)
  }, [requireCognitoUser, replaceCognitoUser, checkEmailVerified, extractUserDetails])

  const beginSetupTOTP = useCallback(async () => {
    return await Auth.setupTOTP(requireCognitoUser())
  }, [requireCognitoUser])

  const confirmSetupTOTP = useCallback(async (totpCode) => {
    const current = requireCognitoUser()
    await Auth.verifyTotpToken(current, totpCode)
    const newUser = await Auth.currentAuthenticatedUser()
    replaceCognitoUser(newUser)
    const emailVerified = await checkEmailVerified(newUser)
    setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
    await extractUserDetails(newUser)
  }, [requireCognitoUser, replaceCognitoUser, checkEmailVerified, extractUserDetails])

  const signOut = useCallback(async () => {
    await signOutInternal()
    navigate('/login')
  }, [signOutInternal, navigate])

  const sendForgotPasswordCode = useCallback(async (email) => {
    await Auth.forgotPassword(email)
  }, [])

  const verifyForgotPasswordCode = useCallback(async (email, code, newPassword) => {
    await Auth.forgotPasswordSubmit(email, code, newPassword)
  }, [])

  const forcePasswordReset = useCallback(async (newPassword) => {
    const updatedUser = await Auth.completeNewPassword(requireCognitoUser(), newPassword)
    replaceCognitoUser(updatedUser)
    return finishAuthOrChallenge(updatedUser)
  }, [requireCognitoUser, replaceCognitoUser, finishAuthOrChallenge])

  const sendEmailVerification = useCallback(async () => {
    await Auth.verifyUserAttribute(requireCognitoUser(), 'email')
  }, [requireCognitoUser])

  const confirmEmailVerification = useCallback(async (code) => {
    await Auth.verifyUserAttributeSubmit(requireCognitoUser(), 'email', code)
    setAuthState(prev => ({ ...prev, emailVerified: true }))
  }, [requireCognitoUser])

  const getUserJWT = useCallback(async () => {
    return getUserJWTInternal()
  }, [])

  const updateUserNames = useCallback(async (firstName, lastName) => {
    const currentUser = await Auth.currentAuthenticatedUser()
    await Auth.updateUserAttributes(currentUser, {
      given_name: firstName,
      family_name: lastName,
    })
    await extractUserDetails(currentUser)
  }, [extractUserDetails])

  const updateEmail = useCallback(async (newEmail) => {
    const currentUser = await Auth.currentAuthenticatedUser()
    await Auth.updateUserAttributes(currentUser, { email: newEmail })
  }, [])

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const currentUser = await Auth.currentAuthenticatedUser()
    await Auth.changePassword(currentUser, currentPassword, newPassword)
  }, [])

  const onTokenRefresh = useCallback((fn) => {
    tokenRefreshListenersRef.current.push(fn)
    return () => {
      tokenRefreshListenersRef.current = tokenRefreshListenersRef.current.filter(f => f !== fn)
    }
  }, [])

  const hasAdminPermissions = useMemo(
    () => groups.some(g => g.toLowerCase() === 'admin'),
    [groups]
  )

  const hasWritePermissions = useMemo(
    () => groups.some(g => {
      const lower = g.toLowerCase()
      return lower === 'admin' || lower === 'manager'
    }),
    [groups]
  )

  const isAuthenticated = authState.isAuthed && authState.emailVerified

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      authState,
      groups,
      signIn,
      signOut,
      confirmSignIn,
      beginSetupTOTP,
      confirmSetupTOTP,
      sendForgotPasswordCode,
      verifyForgotPasswordCode,
      forcePasswordReset,
      sendEmailVerification,
      confirmEmailVerification,
      getUserJWT,
      updateUserNames,
      updateEmail,
      changePassword,
      onTokenRefresh,
      hasAdminPermissions,
      hasWritePermissions,
      isAuthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
